import { httpClient } from '@/lib/http-client'

const API_BASE = import.meta.env.VITE_API_URL || ''

export const aiApi = {
    /** Get current AI provider status */
    getStatus: () => httpClient.get('/ai/status'),

    /** Revaluate question weights via AI — returns SSE stream */
    revaluateWeightsStream: async () => {
        const stored = localStorage.getItem('auth-storage')
        let token = ''
        if (stored) {
            try { token = JSON.parse(stored).state?.accessToken || '' } catch { /* ignore */ }
        }

        const res = await fetch(`${API_BASE}/api/ai/questions/revaluate-weights`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        })

        if (!res.ok) {
            const text = await res.text()
            let msg = `AI revaluation error: ${res.status}`
            try { msg = JSON.parse(text).error || msg } catch { /* use default */ }
            throw new Error(msg)
        }
        return res.body
    },

    /** Generate questions from prompt — returns SSE stream */
    generateQuestionsStream: async (data: {
        prompt: string
        domain_id?: string
        maturity_level?: number
        question_type?: string
        count?: number
    }) => {
        const stored = localStorage.getItem('auth-storage')
        let token = ''
        if (stored) {
            try { token = JSON.parse(stored).state?.accessToken || '' } catch { /* ignore */ }
        }

        const res = await fetch(`${API_BASE}/api/ai/questions/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(data),
        })

        if (!res.ok) {
            const text = await res.text()
            let msg = `AI generation error: ${res.status}`
            try { msg = JSON.parse(text).error || msg } catch { /* use default */ }
            throw new Error(msg)
        }
        return res.body
    },

    /** Generate initiative from prompt + optional files — returns SSE stream */
    generateInitiativeStream: async (data: FormData | { prompt: string; organization_id?: string }) => {
        const stored = localStorage.getItem('auth-storage')
        let token = ''
        if (stored) {
            try { token = JSON.parse(stored).state?.accessToken || '' } catch { /* ignore */ }
        }

        const isFormData = data instanceof FormData
        const res = await fetch(`${API_BASE}/api/ai/initiatives/generate`, {
            method: 'POST',
            headers: {
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: isFormData ? data : JSON.stringify(data),
        })

        if (!res.ok) {
            const text = await res.text()
            let msg = `AI initiative error: ${res.status}`
            try { msg = JSON.parse(text).error || msg } catch { /* use default */ }
            throw new Error(msg)
        }
        return res.body
    },

    /**
     * Stream a chat response via SSE.
     * Returns a ReadableStream — use the `useAIChat` hook for easier usage.
     */
    streamChat: async (messages: { role: string; content: string }[]) => {
        const stored = localStorage.getItem('auth-storage')
        let token = ''
        if (stored) {
            try {
                token = JSON.parse(stored).state?.accessToken || ''
            } catch { /* ignore */ }
        }

        const res = await fetch(`${API_BASE}/api/ai/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ messages }),
        })

        if (!res.ok) {
            throw new Error(`AI chat error: ${res.status}`)
        }

        return res.body
    },
}
